import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, getImageUrl } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import toast from 'react-hot-toast'
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon, PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline'

interface NewsPost {
  _id: string
  type: 'news' | 'story'
  title: string
  titleRu: string
  content?: string
  contentRu?: string
  image?: string
  imageUrl?: string  // для обратной совместимости
  isPremiumOnly: boolean
  isPublished: boolean
  publishedAt?: string
  createdAt: string
}

export default function NewsPage() {
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingNews, setEditingNews] = useState<NewsPost | null>(null)
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const { data, isLoading } = useQuery({
    queryKey: ['news'],
    queryFn: async () => {
      const res = await api.get<{ data: { news: NewsPost[] } }>('/admin/news')
      return res.data.data.news
    },
  })
  
  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      await api.post('/admin/news', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] })
      toast.success('Публикация создана')
      setIsModalOpen(false)
      setUploadedImage(null)
    },
  })
  
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      await api.patch(`/admin/news/${id}`, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] })
      toast.success('Публикация обновлена')
      setEditingNews(null)
      setIsModalOpen(false)
      setUploadedImage(null)
    },
  })
  
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/admin/news/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] })
      toast.success('Публикация удалена')
    },
  })
  
  const togglePublishMutation = useMutation({
    mutationFn: async ({ id, isPublished }: { id: string; isPublished: boolean }) => {
      await api.post(`/admin/news/${id}/publish`, { isPublished })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] })
      toast.success('Статус публикации изменён')
    },
    onError: (error: any) => {
      console.error('Toggle publish error:', error)
      toast.error(error.response?.data?.message || 'Ошибка изменения статуса')
    },
  })
  
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const data = {
      type: formData.get('type') as string,
      title: formData.get('title') as string,
      titleRu: formData.get('titleRu') as string,
      content: formData.get('content') as string,
      contentRu: formData.get('contentRu') as string,
      image: uploadedImage || (formData.get('imageUrl') as string) || '',
      isPremiumOnly: formData.get('isPremiumOnly') === 'on',
      isPublished: formData.get('isPublished') === 'on',
    }
    
    if (editingNews) {
      updateMutation.mutate({ id: editingNews._id, data })
    } else {
      createMutation.mutate(data)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Пожалуйста, выберите изображение')
      return
    }

    const formData = new FormData()
    formData.append('image', file)

    setIsUploading(true)
    try {
      const res = await api.post<{ data: { url: string } }>('/admin/uploads/single?folder=images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      setUploadedImage(res.data.data.url)
      toast.success('Изображение загружено')
    } catch (error) {
      toast.error('Ошибка загрузки изображения')
    } finally {
      setIsUploading(false)
    }
  }

  const handleRemoveImage = () => {
    setUploadedImage(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Новости</h1>
        <button
          onClick={() => {
            setEditingNews(null)
            setUploadedImage(null)
            setIsModalOpen(true)
          }}
          className="btn-primary"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Добавить
        </button>
      </div>
      
      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-full text-center py-8 text-gray-400">
            Загрузка...
          </div>
        ) : data?.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-400">
            Публикации не найдены
          </div>
        ) : (
          data?.map((item) => (
            <div
              key={item._id}
              className={`card ${!item.isPublished ? 'opacity-60' : ''}`}
            >
              {item.image && (
                <img
                  src={getImageUrl(item.image)}
                  alt=""
                  className="w-full h-32 object-cover rounded-lg mb-4"
                />
              )}
              
              <div className="flex items-start justify-between mb-2">
                <span className="badge-blue">Новость</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => togglePublishMutation.mutate({
                      id: item._id,
                      isPublished: !item.isPublished,
                    })}
                    className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                    title={item.isPublished ? 'Снять с публикации' : 'Опубликовать'}
                  >
                    {item.isPublished ? (
                      <EyeIcon className="w-4 h-4 text-green-400" />
                    ) : (
                      <EyeSlashIcon className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setEditingNews(item)
                      setUploadedImage(null)
                      setIsModalOpen(true)
                    }}
                    className="p-1.5 hover:bg-gray-700 rounded transition-colors"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm('Удалить публикацию?')) {
                        deleteMutation.mutate(item._id)
                      }
                    }}
                    className="p-1.5 hover:bg-red-500/20 text-red-400 rounded transition-colors"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <h3 className="font-medium text-white line-clamp-2">
                {item.titleRu || item.title}
              </h3>
              
              {item.contentRu && (
                <p className="text-sm text-gray-400 mt-2 line-clamp-2">
                  {item.contentRu}
                </p>
              )}
              
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700 text-xs text-gray-500">
                <span>
                  {item.publishedAt ? formatDate(item.publishedAt) : 'Не опубликовано'}
                </span>
                {item.isPremiumOnly && (
                  <span className="badge-yellow">Premium</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">
                {editingNews ? 'Редактировать' : 'Новая публикация'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <input type="hidden" name="type" value="news" />

              {/* Загрузчик изображения */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Изображение
                </label>
                
                {/* Показываем превью если есть картинка */}
                {(uploadedImage || editingNews?.image || editingNews?.imageUrl) && (
                  <div className="relative mb-3 inline-block">
                    <img 
                      src={uploadedImage ? getImageUrl(uploadedImage) : getImageUrl(editingNews?.image || editingNews?.imageUrl)} 
                      alt="Preview" 
                      className="h-32 rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Кнопка загрузки */}
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="news-image-upload"
                  />
                  <label
                    htmlFor="news-image-upload"
                    className={`btn-secondary cursor-pointer ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <PhotoIcon className="w-5 h-5 mr-2" />
                    {isUploading ? 'Загрузка...' : (uploadedImage || editingNews?.image || editingNews?.imageUrl ? 'Изменить изображение' : 'Загрузить изображение')}
                  </label>
                </div>

                {/* Скрытое поле для сохранения URL */}
                <input
                  type="hidden"
                  name="imageUrl"
                  value={uploadedImage || editingNews?.image || editingNews?.imageUrl || ''}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Title (EN)
                </label>
                <input
                  type="text"
                  name="title"
                  defaultValue={editingNews?.title}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Заголовок (RU) *
                </label>
                <input
                  type="text"
                  name="titleRu"
                  required
                  defaultValue={editingNews?.titleRu}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Content (EN)
                </label>
                <textarea
                  name="content"
                  rows={3}
                  defaultValue={editingNews?.content}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Содержание (RU)
                </label>
                <textarea
                  name="contentRu"
                  rows={3}
                  defaultValue={editingNews?.contentRu}
                  className="w-full"
                />
              </div>
              
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isPremiumOnly"
                    defaultChecked={editingNews?.isPremiumOnly}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-gray-300">Только для Premium</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isPublished"
                    defaultChecked={editingNews?.isPublished ?? true}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm text-gray-300">Опубликовано</span>
                </label>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false)
                    setEditingNews(null)
                  }}
                  className="btn-secondary"
                >
                  Отмена
                </button>
                <button type="submit" className="btn-primary">
                  {editingNews ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
